/**
 * Node-level analytics shared by generation submit and
 * variant archive events (RFC-1577 §6 Phase 1).
 *
 * `node_type` is always the backend schema type (`image_generator`, not the
 * ReactFlow key `imageGenerator`) so every node event — and the server's
 * `generation_executions` rows — share one vocabulary.
 *
 * Telemetry must never throw or change behaviour: every emitter here swallows
 * its own failures.
 */

import type { ApiRecord, ApiValue } from '../api/responseDecoders'
import { isByomModel } from '../components/canvas-v2/nodes/shared/videoConstraints'
import { createNadDebug } from '../utils/debugLog'
import { FE_TYPE_TO_BACKEND, isFeNodeType } from '../generated/nodeDataTypes'
import { countBucket } from './buckets'
import { track, trackVariantKept } from './events'
import { peekProjectVisitId } from './projectVisit'
import {
  EXECUTE_NODE_ACTION_VALUES,
  type NodeGenerationSubmittedProps,
  type ExecuteNodeAction,
  type GenerationMode,
  type ModelSource,
  type NodePanelTab,
  type VariantArchiveAction,
} from './generated/analyticsEvents'
import { projectTeamContext } from './teamContext'

const log = createNadDebug('dw:analytics-events:node')

const FRONTEND_TYPE_BY_BACKEND: ReadonlyMap<string, string> = new Map(
  Object.entries(FE_TYPE_TO_BACKEND).map(([feType, backendType]) => [backendType, feType]),
)

/**
 * The ONE `node_type` spelling every analytics event uses: the generated
 * frontend node type (`videoGenerator`, `characterDesigner`, …) — the same key
 * `canvas_node_render_failed`, `canvas_variant_kept` and the palette's
 * `option` already send. Accepts either spelling; YDoc rows carry the backend
 * one (`video_generator`).
 */
export function analyticsNodeType(type: string): string {
  if (isFeNodeType(type)) return type
  return FRONTEND_TYPE_BY_BACKEND.get(type) ?? type
}

export function isExecuteNodeAction(value: string): value is ExecuteNodeAction {
  return EXECUTE_NODE_ACTION_VALUES.some((action) => action === value)
}

/** `byom` for a user-registered gateway model, `native` otherwise; unknown → undefined. */
export function modelSourceOf(model: ApiValue): ModelSource | undefined {
  if (typeof model !== 'string' || model.length === 0) return undefined
  return isByomModel(model) ? 'byom' : 'native'
}

/** `initial` when the node has no output yet, `retry` when it already has one. */
export function generateModeFor(hasOutput: boolean): GenerationMode {
  return hasOutput ? 'retry' : 'initial'
}

// ---------------------------------------------------------------------------
// node_generation_submitted
// ---------------------------------------------------------------------------

/**
 * Caller-supplied context for `node_generation_submitted`, carried on
 * `ExecuteNodeOptions.analytics`. Everything is optional: `executeNode()` has
 * 17 callers and only the main Generate / Refine paths populate it.
 */
export interface ExecuteNodeAnalytics {
  nodeType?: string
  tab?: NodePanelTab
  generationMode?: GenerationMode
  /** Overrides the annotation flag derived from `stateUpdates.annotated_image_path`. */
  hasAnnotation?: boolean
  referenceCount?: number
  modelSource?: ModelSource
}

export interface GenerationSubmittedInput {
  requestId: string
  projectId: string
  nodeId: string
  action: string
  count: number
  stateUpdates?: ApiRecord
  analytics?: ExecuteNodeAnalytics
}

function annotationFlag(input: GenerationSubmittedInput): boolean | undefined {
  if (input.analytics?.hasAnnotation !== undefined) return input.analytics.hasAnnotation
  const updates = input.stateUpdates
  if (!updates || !Object.prototype.hasOwnProperty.call(updates, 'annotated_image_path')) return undefined
  const path = updates.annotated_image_path
  return typeof path === 'string' && path.length > 0
}

/** Emitted from `executeNode()` immediately before the request goes out. */
export function trackNodeGenerationSubmitted(input: GenerationSubmittedInput): void {
  try {
    if (!isExecuteNodeAction(input.action)) {
      log({ event: 'generation-submitted-skip', reason: 'unknown-action', node_id: input.nodeId, action: input.action })
      return
    }
    const a = input.analytics
    const props: NodeGenerationSubmittedProps = {
      request_id: input.requestId,
      project_id: input.projectId,
      node_id: input.nodeId,
      action: input.action,
      count: Math.max(1, Math.floor(input.count)),
    }
    if (a?.nodeType) props.node_type = analyticsNodeType(a.nodeType)
    if (a?.tab) props.tab = a.tab
    if (a?.generationMode) props.generation_mode = a.generationMode
    const hasAnnotation = annotationFlag(input)
    if (hasAnnotation !== undefined) props.has_annotation = hasAnnotation
    if (a?.referenceCount !== undefined && Number.isFinite(a.referenceCount)) {
      props.reference_count = Math.max(0, Math.floor(a.referenceCount))
    }
    if (a?.modelSource) props.model_source = a.modelSource
    // The open workspace visit for this project; never mints one.
    const visitId = peekProjectVisitId(input.projectId)
    if (visitId) props.project_visit_id = visitId
    const team = projectTeamContext(input.projectId)
    if (team.owner_type) props.owner_type = team.owner_type
    if (team.team_id) props.team_id = team.team_id
    if (team.actor_role) props.actor_role = team.actor_role
    track('node_generation_submitted', props)
  } catch (err) {
    log({ event: 'generation-submitted-failed', node_id: input.nodeId, error: String(err) })
  }
}

// ---------------------------------------------------------------------------
// canvas_variant_archive_changed
// ---------------------------------------------------------------------------

/**
 * What the Variants grid knows about the variant being archived/restored.
 * Passed to `useVariantMarkers().setArchived` so the event fires from the
 * hook, and only after the marker write succeeded.
 */
export interface VariantArchiveAnalytics {
  nodeType: string
  /** 0-based index into the node's history — the same index `canvas_variant_kept` uses. */
  historyIndex: number
  /** Variants left in the Variants (non-archived) view after the change. */
  variantCountAfter?: number
}

export function trackVariantArchiveChanged(
  projectId: string,
  nodeId: string,
  archived: boolean,
  meta: VariantArchiveAnalytics,
): void {
  try {
    const action: VariantArchiveAction = archived ? 'archive' : 'restore'
    track('canvas_variant_archive_changed', {
      project_id: projectId,
      node_id: nodeId,
      node_type: analyticsNodeType(meta.nodeType),
      action,
      variant_id: `${nodeId}:${meta.historyIndex}`,
      variant_position_bucket: countBucket(meta.historyIndex + 1),
      ...(meta.variantCountAfter !== undefined
        ? { variant_count_bucket: countBucket(meta.variantCountAfter) }
        : {}),
    })
  } catch (err) {
    log({ event: 'variant-archive-track-failed', node_id: nodeId, error: String(err) })
  }
}

// ---------------------------------------------------------------------------
// canvas_variant_kept
// ---------------------------------------------------------------------------

/**
 * The user picked a different variant as the node's kept output (image and
 * video nodes). `variant_id` keeps its original `${nodeId}:${historyIndex}`
 * format; `node_id` / `node_type` are the RFC-1577 enrichment.
 */
export function trackVariantKeptChange(
  projectId: string,
  nodeId: string,
  nodeType: string,
  historyIndex: number,
): void {
  try {
    trackVariantKept({
      project_id: projectId,
      variant_id: `${nodeId}:${historyIndex}`,
      node_id: nodeId,
      node_type: analyticsNodeType(nodeType),
    })
  } catch (err) {
    log({ event: 'variant-kept-track-failed', node_id: nodeId, error: String(err) })
  }
}
